import moment from 'moment';
import * as React from 'react';

import { requestPost, } from '../../../modules/request';
import { Checkbox, DataTable, EXTRA_SMALL_ICON_BASE_SRC, ButtonSpinner, NOT_APPLICABLE, Snackbar, Spinner, Tooltip, defaultSnackbarTimeout, filterIcon, helpIcon, noResultsMessage, smallWhiteCheckIcon } from '../../Shared';
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor';
import { hashPayload, } from '../shared';

const STANDARD_WIDTH = "176px";
const STUDENT_NAME = "Student Name";
const STUDENT_EMAIL = "Student Email";
const SCHOOL = "School";
const GRADE = "Grade";
const TEACHER = "Teacher";
const CLASS = "Class";
const COMPLETED_DATE = "Completed Date";
const ACTIVITY_PACK = "Activity Pack";
const ACTIVITY = "Activity";
const TOOL = "Tool";
const SCORE = "Score";
const STANDARD = "Standard";
const TIME_SPENT = "Time Spent";

const PUSHER_EVENT_KEY = "data-export-cached";
const COMPLETED = "Completed"
const NON_PERCENTAGE_TOOLS = ["Quill Reading for Evidence", "Quill Diagnostic", "Quill Lessons"]
// polling is a fallback in case the Pusher notification that the report is ready never arrives; the last interval repeats
const POLLING_INTERVALS_MS = [5000, 10000, 20000, 30000]
const MAX_POLLING_ATTEMPTS = 10
const DOWNLOAD_SUCCESS_TEXT = "You will receive an email with a download link shortly."
const DOWNLOAD_ERROR_TEXT = "Something went wrong generating your download. Please try again."

interface DataExportTableAndFieldsProps {
  customTimeframeEnd: string;
  customTimeframeStart: string;
  horizontalFilters?: React.ReactNode;
  openMobileFilterMenu: Function;
  pusherChannel?: any;
  queryKey: string;
  searchCount: number;
  selectedClassroomIds: number[];
  selectedGrades: string[];
  selectedSchoolIds: number[];
  selectedTeacherIds: number[];
  selectedTimeframe: string;
}

export const DataExportTableAndFields = ({ queryKey, searchCount, selectedGrades, selectedSchoolIds, selectedTeacherIds, selectedClassroomIds, selectedTimeframe, customTimeframeStart, customTimeframeEnd, openMobileFilterMenu, pusherChannel, horizontalFilters }: DataExportTableAndFieldsProps) => {
  const [showStudentEmail, setShowStudentEmail] = React.useState<boolean>(true);
  const [showSchool, setShowSchool] = React.useState<boolean>(true);
  const [showGrade, setShowGrade] = React.useState<boolean>(true);
  const [showTeacher, setShowTeacher] = React.useState<boolean>(true);
  const [showClass, setShowClass] = React.useState<boolean>(true);
  const [showCompletedDate, setShowCompletedDate] = React.useState<boolean>(true);
  const [showActivityPack, setShowActivityPack] = React.useState<boolean>(true);
  const [showActivity, setShowActivity] = React.useState<boolean>(true);
  const [showTool, setShowTool] = React.useState<boolean>(true);
  const [showScore, setShowScore] = React.useState<boolean>(true);
  const [showStandard, setShowStandard] = React.useState<boolean>(true);
  const [showTimeSpent, setShowTimeSpent] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [generationError, setGenerationError] = React.useState<boolean>(false);
  const [downloadButtonBusy, setDownloadButtonBusy] = React.useState<boolean>(false);
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>(DOWNLOAD_SUCCESS_TEXT);
  const [data, setData] = React.useState<any>(null);
  const [pusherMessage, setPusherMessage] = React.useState<any>(null)
  const [customTimeframeStartString, setCustomTimeframeStartString] = React.useState(null)
  const [customTimeframeEndString, setCustomTimeframeEndString] = React.useState(null)
  const pollingTimeoutRef = React.useRef(null)
  const pollingAttemptsRef = React.useRef(0)
  const fetchIdRef = React.useRef(0)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const fields = {
    [STUDENT_NAME]: {
      dataTableField: { name: STUDENT_NAME, attribute: "student_name", width: STANDARD_WIDTH, renderCellsAsHeaders: true, isSortable: true },
      checked: true
    },
    [STUDENT_EMAIL]: {
      dataTableField: { name: STUDENT_EMAIL, attribute: "student_email", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowStudentEmail,
      checked: showStudentEmail
    },
    [COMPLETED_DATE]: {
      dataTableField: { name: COMPLETED_DATE, attribute: "completed_at", width: STANDARD_WIDTH, isSortable: true, sortAttribute: "completed_at_sort" },
      setterFunction: setShowCompletedDate,
      checked: showCompletedDate
    },
    [ACTIVITY]: {
      dataTableField: { name: ACTIVITY, attribute: "activity_name", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowActivity,
      checked: showActivity
    },
    [ACTIVITY_PACK]: {
      dataTableField: { name: ACTIVITY_PACK, attribute: "activity_pack", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowActivityPack,
      checked: showActivityPack
    },
    [SCORE]: {
      dataTableField: { name: SCORE, attribute: "score", width: STANDARD_WIDTH, isSortable: true, sortAttribute: "score_sort" },
      setterFunction: setShowScore,
      checked: showScore
    },
    [TIME_SPENT]: {
      dataTableField: { name: `${TIME_SPENT} (Mins)`, attribute: "timespent", width: STANDARD_WIDTH, isSortable: true, sortAttribute: "timespent_sort" },
      setterFunction: setShowTimeSpent,
      checked: showTimeSpent
    },
    [STANDARD]: {
      dataTableField: { name: STANDARD, attribute: "standard", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowStandard,
      checked: showStandard
    },
    [TOOL]: {
      dataTableField: { name: TOOL, attribute: "tool", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowTool,
      checked: showTool
    },
    [SCHOOL]: {
      dataTableField: { name: SCHOOL, attribute: "school_name", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowSchool,
      checked: showSchool
    },
    [GRADE]: {
      dataTableField: { name: GRADE, attribute: "classroom_grade", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowGrade,
      checked: showGrade
    },
    [TEACHER]: {
      dataTableField: { name: TEACHER, attribute: "teacher_name", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowTeacher,
      checked: showTeacher
    },
    [CLASS]: {
      dataTableField: { name: CLASS, attribute: "classroom_name", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowClass,
      checked: showClass
    },
  };

  React.useEffect(() => {
    if (!pusherChannel) { return }

    const handlePusherMessage = (body) => setPusherMessage(body.message)
    pusherChannel.bind(PUSHER_EVENT_KEY, handlePusherMessage)

    // Unbind on cleanup: the channel outlives this component while navigating between reports,
    // so an anonymous handler would accumulate stale callbacks across visits.
    return () => pusherChannel.unbind(PUSHER_EVENT_KEY, handlePusherMessage)
  }, [pusherChannel])

  React.useEffect(() => {
    return () => {
      // Invalidate in-flight fetches so a late response can't schedule polling or setState after unmount
      fetchIdRef.current += 1
      clearTimeout(pollingTimeoutRef.current)
    }
  }, [])

  React.useEffect(() => {
    getData()
  }, [searchCount])

  React.useEffect(() => {
    if (!customTimeframeStart) return

    setCustomTimeframeStartString(customTimeframeStart.toISOString())
  }, [customTimeframeStart])

  React.useEffect(() => {
    if (!customTimeframeEnd) return setCustomTimeframeEndString(null)

    setCustomTimeframeEndString(customTimeframeEnd.toISOString())
  }, [customTimeframeEnd])

  React.useEffect(() => {
    if (!pusherMessage) return

    if (filtersMatchHash(pusherMessage)) getData()
  }, [pusherMessage])

  function getData() {
    pollingAttemptsRef.current = 0
    fetchIdRef.current += 1
    setGenerationError(false)
    setLoading(true)
    fetchData(fetchIdRef.current)
  }

  function fetchData(fetchId: number) {
    // a stale fetchId means new filters have superseded this polling chain; the current chain owns the timeout
    if (fetchId !== fetchIdRef.current) { return }

    clearTimeout(pollingTimeoutRef.current)

    const searchParams = {
      query: queryKey,
      timeframe: selectedTimeframe,
      timeframe_custom_start: customTimeframeStart,
      timeframe_custom_end: customTimeframeEnd,
      school_ids: selectedSchoolIds,
      teacher_ids: selectedTeacherIds,
      classroom_ids: selectedClassroomIds,
      grades: selectedGrades
    }

    requestPost('/snapshots/data_export', searchParams, (body) => {
      if (fetchId !== fetchIdRef.current) { return }

      if (!body.hasOwnProperty('results')) {
        setLoading(true)
        schedulePollingAttempt(fetchId)
      } else {
        const { results, } = body
        // We consider `null` to be a lack of data, so if the result is `[]` we need to explicitly `setData(null)`
        const data = results.length > 0 ? results : null
        const formattedData = formatData(data)
        setData(formattedData)
        setLoading(false)
      }
    }, () => handleGenerationFailure(fetchId)).catch(() => handleGenerationFailure(fetchId))
  }

  function handleGenerationFailure(fetchId: number) {
    // Non-2xx responses (error callback) and network failures (rejected promise) never hit the
    // success path, so surface the terminal error state instead of spinning forever
    if (fetchId !== fetchIdRef.current) { return }

    clearTimeout(pollingTimeoutRef.current)
    setLoading(false)
    setGenerationError(true)
  }

  function schedulePollingAttempt(fetchId: number) {
    // Give up after the final attempt with an explicit terminal state so the preview never spins forever
    if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
      setLoading(false)
      setGenerationError(true)
      return
    }

    const interval = POLLING_INTERVALS_MS[Math.min(pollingAttemptsRef.current, POLLING_INTERVALS_MS.length - 1)]
    pollingAttemptsRef.current += 1
    pollingTimeoutRef.current = setTimeout(() => fetchData(fetchId), interval)
  }

  function createCsvReportDownload() {
    // guard against duplicate exports from repeated clicks while a request is in flight
    if (downloadButtonBusy) { return }

    const buttonDisableTime = 2000
    const requestParams = {
      query: 'create_csv_report_download',
      timeframe: selectedTimeframe,
      timeframe_custom_start: customTimeframeStart,
      timeframe_custom_end: customTimeframeEnd,
      school_ids: selectedSchoolIds,
      teacher_ids: selectedTeacherIds,
      classroom_ids: selectedClassroomIds,
      grades: selectedGrades,
      headers_to_display: getHeaders().map(header => header.attribute)
    }
    setDownloadButtonBusy(true)

    const handleDownloadFailure = () => {
      // Re-enable the button and surface feedback on any error path so it isn't stuck disabled
      setSnackbarText(DOWNLOAD_ERROR_TEXT)
      setShowSnackbar(true)
      setDownloadButtonBusy(false)
    }

    requestPost('/snapshots/create_csv_report_download', requestParams, (body) => {
      setSnackbarText(DOWNLOAD_SUCCESS_TEXT)
      setShowSnackbar(true)
      setTimeout(() => { setDownloadButtonBusy(false) }, buttonDisableTime);
    }, handleDownloadFailure).catch(handleDownloadFailure)
  }

  function filtersMatchHash(hashMessage) {
    const filterTarget = [].concat(
      queryKey,
      selectedTimeframe,
      customTimeframeStartString,
      customTimeframeEndString,
      selectedSchoolIds,
      selectedGrades,
      selectedTeacherIds,
      selectedClassroomIds
    )

    const filterHash = hashPayload(filterTarget)

    return hashMessage === filterHash
  }

  function getTimeSpentInMinutes(seconds: number) {
    if (!seconds) {
      return 'N/A';
    }
    if (seconds < 60) {
      return `<1`;
    }
    if (seconds >= 60 && seconds < 120) {
      return '1';
    }
    return `${Math.floor((seconds % 3600) / 60)}`;
  }

  function formatData(data) {
    if (!data) { return null }

    return data.map((entry, index) => {
      const formattedEntry = { ...entry }
      const { score, tool } = entry
      let percentage

      if (NON_PERCENTAGE_TOOLS.includes(tool)) {
        percentage = COMPLETED
      } else if ((isNaN(score) || score < 0) && score !== 0) {
        percentage = NOT_APPLICABLE
      } else {
        percentage = Math.round(parseFloat(score) * 100) + '%'
      }

      formattedEntry.id = index
      // Keep raw values under *_sort keys so the columns sort by true chronological/numeric order
      // rather than by their formatted display strings (referenced via each header's sortAttribute).
      formattedEntry.completed_at_sort = entry.completed_at ? moment(entry.completed_at).valueOf() : null
      formattedEntry.completed_at = moment(entry.completed_at.substring(0, 10)).format("MM/DD/YYYY");
      formattedEntry.timespent_sort = entry.timespent || null
      formattedEntry.timespent = getTimeSpentInMinutes(entry.timespent)
      formattedEntry.score_sort = percentage.endsWith('%') ? parseFloat(score) : null
      formattedEntry.score = percentage
      return formattedEntry
    })
  }

  function toggleCheckbox(e) {
    e.preventDefault();
    const fieldLabel = e.currentTarget.id;
    const checked = fields[fieldLabel].checked;
    const setterFunction = fields[fieldLabel].setterFunction
    setterFunction(!checked);
  }

  function renderCheckboxes() {
    return Object.keys(fields).map((fieldLabel, i) => {
      // skip Student Name because we want this field to always be present
      if (i === 0) { return }

      return (
        <Checkbox
          key={fieldLabel}
          label={fieldLabel}
          onClick={toggleCheckbox}
          selected={fields[fieldLabel].checked}
        />
      )
    })
  }

  function getHeaders() {
    return Object.keys(fields).flatMap(fieldLabel => {
      const headerSelected = fields[fieldLabel].checked
      if (!headerSelected) { return [] }
      return fields[fieldLabel].dataTableField;
    })
  }

  return (
    <React.Fragment>
      <div className="header">
        <Snackbar text={snackbarText} visible={showSnackbar} />
        <h1>
          Student Activity Data Export
        </h1>
        <div className="header-buttons">
          <a className="quill-button outlined small grey focus-on-light" href="https://support.quill.org/en/articles/8672493-how-do-i-use-the-admin-data-export" rel="noopener noreferrer" target="_blank">
            <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`} />
            <span>Guide</span>
          </a>
          <button className="quill-button outlined small grey focus-on-light" disabled={downloadButtonBusy} onClick={createCsvReportDownload} type="button">
            {downloadButtonBusy ? <ButtonSpinner variant="dark" /> : <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />}
            <span>Download</span>
          </button>
        </div>
      </div>
      <div className="filter-button-container">
        <button className="interactive-wrapper focus-on-light" onClick={openMobileFilterMenu} type="button">
          <img alt={filterIcon.alt} src={filterIcon.src} />
          Filters
        </button>
      </div>
      {horizontalFilters}
      <div className="data-export-container">
        <section className="fields-section">
          <h2>Fields</h2>
          <div className="fields-container">
            {renderCheckboxes()}
          </div>
        </section>
        <section className="preview-section">
          <h2>Preview</h2>
          <Tooltip
            tooltipText="This preview is limited to the first 10 results. Your download will include all activities matching your criteria."
            tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
          />
        </section>
        {loading && <Spinner />}
        {!loading && generationError && (
          <div className="generation-error">
            <p>This report is taking longer than expected to generate. Please try again.</p>
            <button className="quill-button outlined small grey focus-on-light" onClick={getData} type="button">
              Try again
            </button>
          </div>
        )}
        {!loading && !generationError && <DataTable
          className="data-export-table"
          defaultSortAttribute="completed_at_sort"
          defaultSortDirection="descending"
          emptyStateMessage={noResultsMessage('activity')}
          headers={getHeaders()}
          reportingFormat={true}
          rows={data || []}
        />}
      </div>
    </React.Fragment>
  )
}

export default DataExportTableAndFields;
